import React, { useRef, useState, useEffect } from 'react'
import { AppState } from '@chegg-wt/core/state'
import { connect } from 'react-redux'
import Icon from '@chegg-wt/core/components/Icon'
import Checkbox from '@chegg-wt/core/components/Checkbox'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { useOnClickOutside } from '@chegg-wt/core/hooks/useOnClickOutside'
import { useToggle } from '@chegg-wt/core/hooks/useToggle'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import {
  selectAllCitations,
  deselectAllCitations,
} from '@chegg-wt/core/state/bibliography/actions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import {
  totalCitationsSelector,
  selectedCitationsSelector,
} from '@chegg-wt/core/state/bibliography/selectors'
import * as Constants from './constants'
import * as TestIds from './testIds'
import * as Styled from './styled'

interface CheckboxMenuProps {
  totalCitations: number
  selectedCitations: Citation[]
  isPremium?: boolean
  selectAllCitations: () => void
  deselectAllCitations: () => void
  onCopy: (returnFocusId: string) => void
  onExport: () => void
  onDelete: () => void
  userClick: (payload: UserClickPayload) => void
}

export const CheckboxMenu: React.FunctionComponent<CheckboxMenuProps> = ({
  totalCitations,
  selectedCitations,
  selectAllCitations,
  deselectAllCitations,
  onCopy,
  onExport,
  onDelete,
  userClick,
  isPremium = false,
}) => {
  const [allCitationsSelected, setAllCitationsSelected] = useState(false)

  useEffect(() => {
    const isSelectedAll = totalCitations === selectedCitations.length
    setAllCitationsSelected(isSelectedAll)
  }, [selectedCitations])

  const { value: isMenuOpen, toggle: toggleMenu, off: closeMenu } = useToggle(false)

  const menu = useRef()
  useOnClickOutside(menu, toggleMenu)

  const handleCopy = (returnFocusId: string) => {
    userClick({ origin: origins.copySelectedCitationsClick })
    onCopy(returnFocusId)
    closeMenu()
  }

  const handleExport = () => {
    userClick({ origin: origins.exportSelectedCitationsClick })
    onExport()
  }

  const handleDelete = () => {
    userClick({ origin: origins.deleteSelectedCitationsClick })
    onDelete()
  }

  return (
    !!selectedCitations.length && (
      <Styled.Menu
        isPremium={isPremium}
        data-test={TestIds.CHECKBOX_MENU_ID}
        role="alert"
      >
        <Styled.StatusContainer>
          <Styled.CheckboxWrap>
            <Checkbox
              id={TestIds.DESELECT_ALL_CITATIONS_ID}
              selected={allCitationsSelected}
              onChange={deselectAllCitations}
              label="deselect all citations"
            >
              {!allCitationsSelected && <Styled.OrangeDash />}
            </Checkbox>
          </Styled.CheckboxWrap>
          <Styled.NumberSelected role="status">{`${selectedCitations.length} selected`}</Styled.NumberSelected>
          {!allCitationsSelected && (
            <Styled.SelectAll
              onClick={selectAllCitations}
              data-test={TestIds.SELECT_ALL_CITATIONS_ID}
            >
              Select all
            </Styled.SelectAll>
          )}
        </Styled.StatusContainer>
        <Styled.DesktopContainer>
          <Styled.ActionButtonContainer>
            <Styled.ActionButton
              onClick={handleExport}
              data-test={TestIds.EXPORT_SELECTED_CITATIONS_ID}
            >
              Export selected
            </Styled.ActionButton>
            <Styled.ActionButton
              id={Constants.DESKTOP_COPY_ID}
              onClick={() => handleCopy(Constants.DESKTOP_COPY_ID)}
              data-test={TestIds.COPY_SELECTED_CITATIONS_ID}
            >
              Copy selected
            </Styled.ActionButton>
            <Styled.ActionButton
              onClick={handleDelete}
              data-test={TestIds.DELETE_SELECTED_CITATIONS_ID}
            >
              Delete selected
            </Styled.ActionButton>
          </Styled.ActionButtonContainer>
        </Styled.DesktopContainer>
        <Styled.MobileContainer>
          <Styled.MenuButton id={Constants.MOBILE_MORE_MENU_ID} onClick={toggleMenu}>
            <Icon name="menu" />
          </Styled.MenuButton>
          {isMenuOpen && (
            <Styled.MenuItemWrapper ref={menu}>
              <Styled.MenuItem
                onClick={handleExport}
                data-test={TestIds.EXPORT_SELECTED_CITATIONS_ID}
              >
                Export selected
              </Styled.MenuItem>
              <Styled.MenuItem
                onClick={() => handleCopy(Constants.MOBILE_MORE_MENU_ID)}
                data-test={TestIds.COPY_SELECTED_CITATIONS_ID}
              >
                Copy selected
              </Styled.MenuItem>
              <Styled.MenuItem
                onClick={handleDelete}
                data-test={TestIds.DELETE_SELECTED_CITATIONS_ID}
              >
                Delete selected
              </Styled.MenuItem>
            </Styled.MenuItemWrapper>
          )}
        </Styled.MobileContainer>
      </Styled.Menu>
    )
  )
}

const mapStateToProps = (state: AppState) => ({
  totalCitations: totalCitationsSelector(state),
  selectedCitations: selectedCitationsSelector(state),
})

const mapDispatchToProps = {
  selectAllCitations,
  deselectAllCitations,
  userClick,
}

export default connect(mapStateToProps, mapDispatchToProps)(CheckboxMenu)
